// src/pages/admin/EditHero.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { FaTimes } from "react-icons/fa";

const EditHero = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const API_URL = process.env.REACT_APP_API_URL;

  // ✅ IMPORTANT: make cookie key consistent across your project
  const token = Cookies.get("gameport"); // <-- change to "gamesport" everywhere if that's your real key

  const [formData, setFormData] = useState({ title: "", subtitle: "" });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [errors, setErrors] = useState({});

  const inputBase =
    "w-full mt-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-gray-800 " +
    "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300";

  // ----------------------------
  // Fetch current hero
  // ----------------------------
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["hero", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/admin/hero/active/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.message || "Failed to load hero");
      return result;
    },
  });

  useEffect(() => {
    if (data?.hero) {
      setFormData({
        title: data.hero.title || "",
        subtitle: data.hero.subtitle || "",
      });
      setPreview(data.hero.image || "");
    }
  }, [data]);

  // ✅ Cleanup preview object URL
  useEffect(() => {
    return () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  // ----------------------------
  // Update mutation
  // ----------------------------
  const { mutate, isPending: updating, isError: updateError, error: updateErr } =
    useMutation({
      mutationKey: ["hero-update", id],
      mutationFn: async () => {
        if (!token) throw new Error("You are not authenticated.");

        const fd = new FormData();
        fd.append("title", formData.title.trim());
        fd.append("subtitle", formData.subtitle.trim());
        if (image) fd.append("image", image);

        const res = await fetch(`${API_URL}/admin/hero/${id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });

        const result = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(result.message || "Failed to update hero");
        return result;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["hero-active"] });
        queryClient.invalidateQueries({ queryKey: ["hero", id] });
        navigate("/");
      },
    });

  const validate = () => {
    const e = {};
    if (!formData.title.trim()) e.title = "Title is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    mutate();
  };

  if (isPending) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <p className="text-sm text-gray-600">Loading...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <p className="text-red-600 text-sm">{error?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-7 md:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            Edit Hero
          </h2>
          <p className="text-center text-sm sm:text-base text-gray-500 mt-1">
            Update the hero title, subtitle, and image.
          </p>

          <form onSubmit={handleSubmit} className="grid gap-5 sm:gap-6 mt-7 sm:mt-8">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-gray-700">Title</label>
              <input
                value={formData.title}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, title: e.target.value }))
                }
                className={inputBase}
                placeholder="Enter hero title"
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Subtitle */}
            <div>
              <label className="text-sm font-medium text-gray-700">Subtitle</label>
              <textarea
                rows={4}
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, subtitle: e.target.value }))
                }
                className={inputBase}
                placeholder="Enter hero subtitle (optional)"
              />
            </div>

            {/* Image */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Replace Image (optional)
              </label>

              {preview && (
                <div className="relative mb-4 rounded-2xl overflow-hidden border border-gray-200">
                  <img
                    src={preview}
                    alt="preview"
                    className="w-full object-cover h-44 sm:h-56 md:h-64"
                  />

                  {image && (
                    <button
                      type="button"
                      onClick={() => {
                        setImage(null);
                        setPreview(data?.hero?.image || "");
                      }}
                      className="absolute top-3 right-3 bg-red-600 text-white p-2 rounded-full shadow hover:bg-red-700"
                      aria-label="Remove selected image"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                <input
                  type="file"
                  accept="image/*"
                  className="block w-full text-sm text-gray-700
                    file:mr-4 file:rounded-lg file:border-0
                    file:bg-gray-900 file:px-4 file:py-2 file:text-white
                    hover:file:opacity-90"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);

                    setImage(file);
                    setPreview(URL.createObjectURL(file));
                  }}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Upload a new wide banner image if you want to replace the current one.
                </p>
              </div>
            </div>

            {/* Mutation error */}
            {updateError && (
              <p className="text-red-600 text-sm">{updateErr?.message}</p>
            )}

            <button
              type="submit"
              disabled={updating}
              className={`w-full py-3 rounded-xl font-semibold transition shadow-sm ${
                updating
                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700 active:bg-red-800 text-white"
              }`}
            >
              {updating ? "Updating..." : "Update Hero"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditHero;
